import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, User, Search, LogOut, Package } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useCategories } from '../context/CategoriesContext';
import { useSettings } from '../context/SettingsContext';
import NotificationsBell from './NotificationsBell';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  const { token: customerToken, displayName, logout, authHeaders } = useCustomerAuth();
  const { settings } = useSettings();
  const { categories } = useCategories();
  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl border-b border-neutral-200/70'
          : 'bg-[#F8FAFC]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src={settings?.logo_image || ""}
              alt="Mohammed Riaz & Partner LLC"
              className="h-9 w-auto rounded-md group-hover:scale-105 transition-transform object-contain"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-display text-[15px] font-bold tracking-tight text-[#0F172A]">
                Mohammed Riaz
              </span>
              <span className="text-[9px] font-semibold text-[#0078D4] tracking-[0.2em] uppercase mt-0.5">
                &amp; Partner LLC
              </span>
            </div>
          </Link>

          {/* Center: Navigation */}
          {!isAdmin && (
            <nav className="hidden lg:flex items-center gap-7">
              <Link to="/" className="text-[14px] font-medium text-[#0F172A] underline-hover">
                Home
              </Link>
            </nav>
          )}

          {/* Right: actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              className="hidden sm:flex p-2 hover:bg-neutral-100 rounded-full transition-colors"
              aria-label="Search"
            >
              <Search className="w-4 h-4 text-[#0F172A]" />
            </button>
            {customerToken ? (
              <>
                <NotificationsBell role="customer" authHeaders={authHeaders} />
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="inline-flex items-center gap-2 pl-1.5 pr-2.5 sm:pl-2 sm:pr-3 py-1.5 rounded-full hover:bg-neutral-100 transition-colors text-[#0F172A]"
                  data-testid="user-menu-trigger"
                >
                  <span className="w-7 h-7 rounded-full bg-[#0078D4] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                  <span className="text-[12px] sm:text-[13px] font-medium max-w-[110px] sm:max-w-[150px] truncate">
                    Hello, <span className="font-semibold">{displayName}</span>
                  </span>
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden z-50 py-1">
                    <div className="px-4 py-3 border-b border-neutral-100">
                      <div className="text-sm font-semibold text-[#0F172A] truncate">Hello, {displayName}</div>
                      <div className="text-[11px] text-neutral-500 truncate">Signed in</div>
                    </div>
                    <Link
                      to="/my-orders"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#0F172A] hover:bg-neutral-50"
                      data-testid="menu-my-orders"
                    >
                      <Package className="w-4 h-4 text-neutral-500" /> My orders
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                      data-testid="menu-signout"
                    >
                      <LogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                )}
              </div>
              </>
            ) : (
              <Link
                to="/login"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-neutral-100 transition-colors text-[13px] font-medium text-[#0F172A]"
                aria-label="Sign in"
                data-testid="navbar-signin"
              >
                <User className="w-4 h-4" />
                Sign in
              </Link>
            )}
            <button
              onClick={() => setCartOpen(true)}
              className="relative flex items-center gap-1.5 px-3.5 sm:px-4 py-2 rounded-full bg-[#0F172A] hover:bg-[#0078D4] text-white text-[13px] font-medium transition-colors"
              aria-label="Cart"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span className="w-5 h-5 rounded-full bg-[#0078D4] text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white sm:ring-0 sm:bg-white sm:text-[#0F172A]">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
